import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { portalApi } from '../services/portalApi';
import { useApiMutation } from '../hooks/useApiMutation';
import { queryKeys } from './queryKeys';

const FIVE_MIN = 5 * 60 * 1000;

export const assigneeName = (a) => (a ? [a.firstName, a.lastName].filter(Boolean).join(' ') || a.email : '');

export const shortName = (a) => (a?.firstName && a.lastName ? `${a.firstName[0]}. ${a.lastName}` : assigneeName(a));

export function useBookingsQuery({ from, to }) {
  const params = { from, to };
  return useQuery({
    queryKey: queryKeys.scheduling.bookingList(params),
    queryFn: () => portalApi.bookings(params),
    select: (res) => res.bookings || [],
    placeholderData: keepPreviousData,
    staleTime: 30_000
  });
}

export function useBookingAssigneesQuery() {
  return useQuery({
    queryKey: queryKeys.scheduling.assignees(),
    queryFn: portalApi.bookingAssignees,
    select: (res) => res.assignees || [],
    staleTime: FIVE_MIN
  });
}

export function useAvailabilityQuery({ from, days }, { enabled = true } = {}) {
  const params = { from, days };
  return useQuery({
    queryKey: queryKeys.scheduling.availability(params),
    queryFn: () => portalApi.schedulingAvailability(params),
    select: (res) => res.days || [],
    enabled,
    staleTime: 60_000
  });
}

const toBookingPayload = ({ assignedTo, ...rest }) =>
  assignedTo === undefined ? rest : { ...rest, assignedTo: assignedTo?._id || null };

const bookingToast = ({ changes }) => {
  if (changes.status === 'cancelled') return 'Booking cancelled';
  if (changes.status === 'completed') return 'Marked as completed';
  if (changes.start) return 'Booking rescheduled';
  if (changes.assignedTo !== undefined) {
    return changes.assignedTo ? `Assigned to ${assigneeName(changes.assignedTo)}` : 'Booking unassigned';
  }
  return 'Booking updated';
};

export function useCreateBookingMutation() {
  return useApiMutation({
    mutationFn: (payload) => portalApi.createBooking(payload),
    invalidate: [queryKeys.scheduling.bookings(), queryKeys.scheduling.availabilities()],
    success: (_data, { type }) => (type === 'block' ? 'Time blocked' : 'Tour booked'),
    error: 'Could not save this booking'
  });
}

export function useUpdateBookingMutation() {
  return useApiMutation({
    mutationFn: ({ id, changes }) => portalApi.updateBooking(id, toBookingPayload(changes)),
    optimistic: ({ id, changes }) => [
      {
        key: queryKeys.scheduling.bookings(),
        update: (data) => ({
          ...data,
          bookings: data.bookings.map((b) => (b._id === id ? { ...b, ...changes } : b))
        })
      }
    ],
    invalidate: [queryKeys.scheduling.bookings(), queryKeys.scheduling.availabilities()],
    success: (_data, variables) => bookingToast(variables),
    error: 'Could not update this booking'
  });
}

const saveScheduling = async (changes) => {
  const res = await portalApi.updateScheduling(changes);
  if (res.warning) toast.warning(res.warning);
  return res;
};

export function useUpdateSchedulingMutation() {
  return useApiMutation({
    mutationFn: saveScheduling,
    optimistic: (changes) => [
      {
        key: queryKeys.me,
        update: (me) => ({
          ...me,
          organization: { ...me.organization, scheduling: { ...me.organization?.scheduling, ...changes } }
        })
      }
    ],
    invalidate: [queryKeys.me, queryKeys.scheduling.availabilities()],
    success: (data) => (data.warning ? null : 'Scheduling rules saved'),
    error: 'Could not save scheduling rules'
  });
}
